import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '@/services/api';
import toast from 'react-hot-toast';
import PrintableStudentProfile from '@/components/PrintableStudentProfile';

const StudentEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const classes = ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'];
  const genders = ['Male', 'Female'];
  const relationships = ['Father', 'Mother', 'Guardian'];
  const statuses = ['pending', 'approved', 'rejected'];

  // Helper function to format date for HTML input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        const res = await adminAPI.getStudentById(id);
        setStudent(res.data.data);
      } catch (err) {
        toast.error('Student not found');
        navigate('/admin/students');
      } finally {
        setLoading(false);
      }
    };
    fetchStudent();
  }, [id, navigate]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!student.first_name?.trim()) newErrors.first_name = 'First name is required';
    if (!student.last_name?.trim()) newErrors.last_name = 'Last name is required';
    if (!student.email?.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(student.email)) newErrors.email = 'Invalid email format';
    if (!student.phone?.trim()) newErrors.phone = 'Phone is required';
    if (!student.date_of_birth) newErrors.date_of_birth = 'Date of birth is required';
    if (!student.gender) newErrors.gender = 'Gender is required';
    if (!student.class) newErrors.class = 'Class is required';
    if (!student.parent_name?.trim()) newErrors.parent_name = 'Parent/Guardian name is required';
    if (!student.parent_phone?.trim()) newErrors.parent_phone = 'Parent/Guardian phone is required';
    if (!student.status) newErrors.status = 'Status is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('=== SUBMIT TRIGGERED ===');
    console.log('Student data:', student);
    console.log('Validation result:', validateForm());
    
    if (!validateForm()) {
      console.log('Validation failed, errors:', errors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setSaving(true);
      console.log('Calling API with id:', id);
      console.log('Token:', localStorage.getItem('token'));
      
      const res = await adminAPI.updateStudent(id, student);
      console.log('API response:', res);
      
      toast.success('Student profile updated successfully');
      navigate('/admin/students');
    } catch (err) {
      console.log('API error:', err);
      console.log('Error response:', err.response?.data);
      console.log('Error status:', err.response?.status);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/admin/login');
        return;
      }
      toast.error(err.response?.data?.message || 'Failed to update student');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setStudent({ ...student, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Edit Student Profile</h1>
          <p className="text-slate-500 mt-1">Update {student?.full_name || ''}'s information</p>
        </div>
        <button
          onClick={() => navigate('/admin/students')}
          className="px-6 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-all flex items-center gap-2"
        >
          <i className="fas fa-arrow-left" />
          Back to Students
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Section 1: Personal Information */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-2xl flex items-center justify-center text-primary font-bold">
              1
            </div>
            Personal Information
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">First Name *</label>
              <input
                name="first_name"
                value={student?.first_name || ''}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-4 focus:ring-primary/10 outline-none transition-all font-medium ${
                  errors.first_name ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : 'border-slate-200 focus:border-primary focus:ring-primary/20'
                }`}
              />
              {errors.first_name && <p className="text-red-600 text-sm mt-1">{errors.first_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Last Name *</label>
              <input
                name="last_name"
                value={student?.last_name || ''}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-4 focus:ring-primary/10 outline-none transition-all font-medium ${
                  errors.last_name ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : 'border-slate-200 focus:border-primary focus:ring-primary/20'
                }`}
              />
              {errors.last_name && <p className="text-red-600 text-sm mt-1">{errors.last_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Middle Name</label>
              <input
                name="middle_name"
                value={student?.middle_name || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/20 outline-none transition-all font-medium"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Email *</label>
              <div className="text-sm font-semibold text-slate-700 mb-2">Email (Read-only)</div>
              <input
                type="email"
                name="email"
                value={student?.email || ''}
                className="w-full px-4 py-3 rounded-xl border-2 bg-slate-50 cursor-not-allowed border-slate-200 text-slate-500"
                readOnly
              />
              {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Phone Number *</label>
              <input
                name="phone"
                value={student?.phone || ''}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-4 focus:ring-primary/10 outline-none transition-all font-medium ${
                  errors.phone ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : 'border-slate-200 focus:border-primary focus:ring-primary/20'
                }`}
              />
              {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Date of Birth *</label>
              <input
                type="date"
                name="date_of_birth"
                value={formatDateForInput(student?.date_of_birth)}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-4 focus:ring-primary/10 outline-none transition-all font-medium ${
                  errors.date_of_birth ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : 'border-slate-200 focus:border-primary focus:ring-primary/20'
                }`}
              />
              {errors.date_of_birth && <p className="text-red-600 text-sm mt-1">{errors.date_of_birth}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Gender *</label>
              <select
                name="gender"
                value={student?.gender || ''}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-4 focus:ring-primary/10 outline-none transition-all font-medium ${
                  errors.gender ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : 'border-slate-200 focus:border-primary focus:ring-primary/20'
                }`}
              >
                <option value="">Select Gender</option>
                {genders.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
              {errors.gender && <p className="text-red-600 text-sm mt-1">{errors.gender}</p>}
            </div>
            <div className="lg:col-span-2">
              <label className="block text-sm font-semibold text-slate-700 mb-2">Home Address</label>
              <textarea
                name="home_address"
                rows="3"
                value={student?.home_address || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/20 outline-none transition-all font-medium resize-vertical"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Religion</label>
              <input
                name="religion"
                value={student?.religion || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/20 outline-none transition-all font-medium"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">State of Origin</label>
              <input
                name="state_of_origin"
                value={student?.state_of_origin || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/20 outline-none transition-all font-medium"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">LGA</label>
              <input
                name="lga"
                value={student?.lga || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/20 outline-none transition-all font-medium"
              />
            </div>
          </div>
        </div>

        {/* Section 2: Parent/Guardian Information */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-2xl flex items-center justify-center text-primary font-bold">
              2
            </div>
            Parent/Guardian Information
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Parent/Guardian Full Name *</label>
              <input
                name="parent_name"
                value={student?.parent_name || ''}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-4 focus:ring-primary/10 outline-none transition-all font-medium ${
                  errors.parent_name ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : 'border-slate-200 focus:border-primary focus:ring-primary/20'
                }`}
              />
              {errors.parent_name && <p className="text-red-600 text-sm mt-1">{errors.parent_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Parent/Guardian Phone *</label>
              <input
                name="parent_phone"
                value={student?.parent_phone || ''}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-4 focus:ring-primary/10 outline-none transition-all font-medium ${
                  errors.parent_phone ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : 'border-slate-200 focus:border-primary focus:ring-primary/20'
                }`}
              />
              {errors.parent_phone && <p className="text-red-600 text-sm mt-1">{errors.parent_phone}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Parent/Guardian Email</label>
              <input
                type="email"
                name="parent_email"
                value={student?.parent_email || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/20 outline-none transition-all font-medium"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Relationship</label>
              <select
                name="relationship"
                value={student?.relationship || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/20 outline-none transition-all font-medium"
              >
                <option value="">Select Relationship</option>
                {relationships.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Section 3: Academic Information */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-2xl flex items-center justify-center text-primary font-bold">
              3
            </div>
            Academic Information
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Class/Programme *</label>
              <select
                name="class"
                value={student?.class || ''}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-4 focus:ring-primary/10 outline-none transition-all font-medium ${
                  errors.class ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : 'border-slate-200 focus:border-primary focus:ring-primary/20'
                }`}
              >
                <option value="">Select Class</option>
                {classes.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.class && <p className="text-red-600 text-sm mt-1">{errors.class}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Previous School</label>
              <input
                name="previous_school"
                value={student?.previous_school || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/20 outline-none transition-all font-medium"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Session</label>
              <input
                name="session"
                value={student?.session || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/20 outline-none transition-all font-medium"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Application Status *</label>
              <select
                name="status"
                value={student?.status || ''}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-4 focus:ring-primary/10 outline-none transition-all font-medium ${
                  errors.status ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : 'border-slate-200 focus:border-primary focus:ring-primary/20'
                }`}
              >
                <option value="">Select Status</option>
                {statuses.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
              {errors.status && <p className="text-red-600 text-sm mt-1">{errors.status}</p>}
            </div>
          </div>
        </div>

        {/* Section 4: Profile Photo */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-2xl flex items-center justify-center text-primary font-bold">
              4
            </div>
            Profile Photo
          </h2>
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-slate-700 mb-4">Upload new passport photo</label>
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    try {
                      const formData = new FormData();
                      formData.append('picture', file);
                      
                      const response = await adminAPI.uploadStudentPicture(formData);
                      setStudent({ ...student, picture: response.data.data.url });
                      toast.success('Photo uploaded successfully');
                    } catch (err) {
                      console.error('Upload error:', err);
                      toast.error('Failed to upload photo');
                    }
                  }
                }}
                className="w-full px-4 py-3 border-2 border-dashed border-slate-300 rounded-xl hover:border-primary transition-all cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-dark"
              />
              <p className="text-xs text-slate-500 mt-2">PNG, JPG up to 2MB. Passport size recommended.</p>
            </div>
            <div className="flex-shrink-0">
              <p className="text-sm font-semibold text-slate-700 mb-4">Current Photo</p>
              {student?.picture && typeof student.picture === 'string' && student.picture.trim() ? (
                <div className="w-32 h-32 rounded-2xl overflow-hidden bg-slate-100 flex items-center justify-center border-4 border-slate-200">
                  <img src={student.picture} alt="Student" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-32 h-32 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400">
                  <i className="fas fa-image text-3xl" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Form Buttons */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 pt-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-end">
            <button
              type="button"
              onClick={() => navigate('/admin/students')}
              className="flex-1 sm:flex-none px-8 py-4 bg-slate-100 text-slate-700 rounded-2xl font-bold text-lg hover:bg-slate-200 transition-all shadow-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 sm:flex-none px-8 py-4 bg-gradient-to-r from-primary to-primary-dark text-white rounded-2xl font-bold text-lg hover:from-primary-dark hover:to-primary shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : 'Save Changes'}
            </button>
          </div>
        </div>
        <PrintableStudentProfile student={student} />
      </form>
    </div>
  );
};

export default StudentEdit;

